import { Injectable } from "@angular/core";
import { AuthenticationService } from "./authentication.service";
import Swal, { SweetAlertResult } from "sweetalert2";
import { ProjectionOperation } from "../models/vcloud-api";
import { TenantService } from "./tenant.service";
import { parse, unparse } from "papaparse";

const dateBoundaryStart = new Date("2000-01-01");
const dateBoundaryEnd = new Date("2050-01-01");

@Injectable({
  providedIn: "root",
})
export class DataService {
  public alphaRegExp = /[a-zA-Z]/g;
  public dashRegExp = /^\-/;
  public accountIdRegExp = /^'?"?[0-9]{12}'?"?$/;

  constructor(private auth: AuthenticationService, private tenant: TenantService) {}

  public rollingMean(numbers: number[]): number[] {
    const means = [];
    for (let i = 1; i <= numbers.length; i++) {
      const slice = numbers.slice(0, i);
      means.push(this.mean(slice));
    }
    return means;
  }

  public linearRegression(yValues: number[]): number[] {
    const n = yValues.length;
    if (n === 0) return [];
    let sumY = 0;
    let sumX = 0;
    let sumX2 = 0;
    let sumXY = 0;
    for (let i = 0; i < n; i++) {
      const x = i;
      const y = yValues[i];
      sumX += x;
      sumY += y;
      sumX2 += x * x;
      sumXY += x * y;
    }
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
    const intercept = (sumY - slope * sumX) / n;
    return yValues.map((_, i) => intercept + slope * i);
  }

  public mean(numbers: number[]): number {
    return (numbers ?? []).reduce((a, b) => a + (b ?? 0), 0) / (numbers ?? [])?.length;
  }

  public getValueInBetween(low: number, high: number, percentage: number): number {
    return percentage * (high - low) + low;
  }

  public hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0"); // convert to Hex and prefix "0" if needed
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  /**
   * Efficiently extracts the header row from a CSV string using Papa Parse.
   *
   * @param csvString The raw CSV string to parse.
   * @returns An array of strings representing the headers, or an empty array if none are found.
   */
  public getCSVHeaders(csvString: string): string[] {
    // Trim the input to handle cases where the string might just be whitespace.
    const trimmedCsv = csvString.trim();
    if (!trimmedCsv) {
      return [];
    }

    // Use the 'preview' option to instruct Papa Parse to only read the first row.
    // This is highly efficient for large files as it stops parsing immediately.
    const result = parse(trimmedCsv, {
      preview: 1, // Only parse the first non-empty row
      skipEmptyLines: true, // Ignore any leading blank lines
    });

    // The result.data will be an array containing one element: the array of headers.
    // e.g., [['Header A', 'Header B', 'Header C']]
    // We return the first element, or an empty array if parsing yielded no data.
    return (result.data[0] as string[]) || [];
  }

  public convertCsvTableToPivotTable(
    csvString: string,
    indexAttr: string,
    indexAttr2: string | undefined,
    columnAttr: string,
    valueAttr: string,
    aggFunction: ProjectionOperation
  ): string {
    const parseResult = parse(csvString, {
      header: true,
      skipEmptyLines: true,
    });

    if (parseResult.errors.length) {
      throw new Error(`CSV Parsing Error: ${parseResult.errors[0].message}`);
    }

    const data = parseResult.data as { [key: string]: string }[];
    if (data.length === 0 || !parseResult.meta.fields) {
      return "";
    }

    const headers: string[] = parseResult.meta.fields;

    // --- CHANGE 2: Validate all required headers, including the optional one if provided ---
    const requiredHeaders = [indexAttr, columnAttr, valueAttr];
    if (indexAttr2) {
      requiredHeaders.push(indexAttr2);
    }
    for (const header of requiredHeaders) {
      if (!headers.includes(header)) {
        throw new Error(`Required attribute "${header}" not found in CSV headers. Found: ${headers.join(", ")}`);
      }
    }

    // --- CHANGE 3: Add date parsing logic for the optional second index ---
    // (This block is condensed for brevity but follows the same pattern as the original)
    const attrsToDateCheck = [indexAttr, columnAttr];
    if (indexAttr2) {
      attrsToDateCheck.push(indexAttr2);
    }
    for (const attr of attrsToDateCheck) {
      const validDateValues: number =
        (data || []).filter((row) => {
          const v: string = row[attr];
          if (!v) {
            return false;
          }
          const d = new Date(v);
          return d.toString() !== "Invalid Date" && d < dateBoundaryEnd && d >= dateBoundaryStart;
        })?.length ?? 0;
      const isDate: boolean = validDateValues / (data?.length ?? 0) >= 0.25;
      if (isDate) {
        for (const row of data) {
          try {
            const d = new Date(row[attr]);
            if (d.toString() !== "Invalid Date") {
              row[attr] = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d
                .getDate()
                .toString()
                .padStart(2, "0")}`;
            }
          } catch (e) {
            /* ignore parse errors */
          }
        }
      }
    }

    const groupedData: { [key: string]: { [key: string]: number[] } } = {};
    for (const row of data) {
      const value: number = parseFloat(row[valueAttr]);
      if (isNaN(value)) continue;

      // --- CHANGE 4: Create a composite key if indexAttr2 is used ---
      // We use a unique separator to avoid clashes with data.
      const indexValue1 = row[indexAttr];
      const compositeKey = indexAttr2 ? `${indexValue1}::pivot::${row[indexAttr2]}` : indexValue1;

      const columnValue = row[columnAttr];
      if (!compositeKey || !columnValue) continue;

      if (!groupedData[compositeKey]) {
        groupedData[compositeKey] = {};
      }
      if (!groupedData[compositeKey][columnValue]) {
        groupedData[compositeKey][columnValue] = [];
      }
      groupedData[compositeKey][columnValue].push(value);
    }

    const uniqueColumns: string[] = [...new Set(data.map((row) => row[columnAttr]))].sort();
    const aggregatedData: { [key: string]: { [key: string]: number | null } } = {};
    const sortedIndexValues = Object.keys(groupedData).sort();

    // The aggregation logic itself doesn't need to change, as it just works on the keys.
    for (const compositeKey of sortedIndexValues) {
      aggregatedData[compositeKey] = {};
      for (const columnValue of uniqueColumns) {
        const values = groupedData[compositeKey]?.[columnValue];
        if (values && values.length > 0) {
          switch (aggFunction) {
            // ... (sum, avg, count, etc. - no changes here)
            case ProjectionOperation.sum:
              aggregatedData[compositeKey][columnValue] = values.reduce((a, b) => a + b, 0);
              break;
            case ProjectionOperation.avg:
              aggregatedData[compositeKey][columnValue] = values.reduce((a, b) => a + b, 0) / values.length;
              break;
            case ProjectionOperation.count:
              aggregatedData[compositeKey][columnValue] = values.length;
              break;
            case ProjectionOperation.min:
              aggregatedData[compositeKey][columnValue] = Math.min(...values);
              break;
            case ProjectionOperation.max:
              aggregatedData[compositeKey][columnValue] = Math.max(...values);
              break;
            default:
              aggregatedData[compositeKey][columnValue] = values.reduce((a, b) => a + b, 0);
          }
        } else {
          aggregatedData[compositeKey][columnValue] = null;
        }
      }
    }

    // --- CHANGE 5: Reconstruct the multi-index object for Papaparse ---
    const pivotDataForUnparse = sortedIndexValues.map((compositeKey) => {
      const rowObject: { [key: string]: any } = {};

      // If we used a composite key, split it back into the respective index columns.
      if (indexAttr2) {
        const parts = compositeKey.split("::pivot::");
        rowObject[indexAttr] = parts[0];
        rowObject[indexAttr2] = parts[1];
      } else {
        rowObject[indexAttr] = compositeKey;
      }

      for (const columnValue of uniqueColumns) {
        rowObject[columnValue] = aggregatedData[compositeKey][columnValue] ?? "";
      }
      return rowObject;
    });

    // --- CHANGE 6: Define the final, ordered list of columns for the output CSV ---
    const finalColumns = indexAttr2 ? [indexAttr, indexAttr2, ...uniqueColumns] : [indexAttr, ...uniqueColumns];

    return unparse(pivotDataForUnparse, {
      columns: finalColumns, // Ensures correct column order
    });
  }

  public async download(data: string, fileName: string, mime = "text/csv", isPivot = false): Promise<void> {
    if (this.auth.tenant === "trial") {
      Swal.fire({
        title: "Not Allowed",
        text: "This feature is not allowed for your tenant.",
        icon: "warning",
        allowOutsideClick: true,
      });
      return;
    }

    let csp: string;
    try {
      csp = location.pathname.split("/")[3].toUpperCase();
    } catch (e) {
      try {
        csp = location.pathname.split("/").slice(-1)[0].toUpperCase();
      } catch (e2) {
        try {
          csp = location.pathname
            .split("/")
            .find((w) => w.length === 3)
            .toUpperCase();
        } catch (e3) {}
      }
    }

    if (!csp) {
      csp = "Portal";
    }

    // inject csp if not supplied
    if (
      !fileName.startsWith(csp) &&
      !fileName.startsWith(csp.toLowerCase()) &&
      !fileName.startsWith(csp.toUpperCase())
    ) {
      try {
        return this.download(data, `${csp}-${fileName.replace(new RegExp(`-${csp}-`, "i"), "")}`, mime, isPivot);
      } catch (e) {}
    }

    // inject date if not supplied
    if (!fileName.match(/\d{4}[-]\d{2}[-]\d{2}/)) {
      try {
        const ext = fileName.split(".").slice(-1)[0];
        const stem = fileName.split(".")[0];
        return this.download(data, `${stem}-${new Date().toISOString().split(".")[0]}.${ext}`, mime, isPivot);
      } catch (e) {}
    }

    // inject report title
    try {
      const reportWords = location.pathname
        .split("/")
        .map((t) => decodeURIComponent(t).split(" "))
        .flat()
        .filter((w) => w && w.length > 3)
        .filter((w) => w.toLowerCase() !== csp.toLowerCase());
      if (!reportWords.every((w) => fileName.toLowerCase().includes(w.toLowerCase()))) {
        return this.download(
          data,
          `${csp}-${reportWords.map((w) => w.substring(0, 1).toUpperCase() + w.substring(1)).join("-")}-${fileName}`,
          mime,
          isPivot
        );
      }
    } catch (e) {}

    let parseCSV = data;
    try {
      parseCSV = data
        .split("\n")
        .map((row) => {
          return row
            .split(",")
            .map((col) => {
              if (this.alphaRegExp.test(col)) {
                return col.replace(this.dashRegExp, " -");
              } else {
                return this.accountIdRegExp.test(col.trim()) ? `${col}\t` : col;
              }
            })
            .join(",");
        })
        .join("\n");
    } catch (e) {}

    if (!isPivot && mime === "text/csv") {
      const headers: string[] = this.getCSVHeaders(data);

      if (this.isEligibleForPivotHeaders(headers) && this.isEligibleForPivotRows(data)) {
        const response: SweetAlertResult = await Swal.fire({
          title: "What type of table would you like?",
          allowEscapeKey: false,
          allowOutsideClick: false,
          backdrop: true,
          cancelButtonColor: this.tenant.colors.info,
          cancelButtonText: "Pivot",
          confirmButtonColor: this.tenant.colors.primary,
          confirmButtonText: "Regular",
          showCancelButton: true,
          showConfirmButton: true,
        });

        if (!response.isConfirmed) {
          const responsePivot = await Swal.fire({
            title: "Pivot Table Configuration",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: true,
            backdrop: true,
            confirmButtonColor: this.tenant.colors.primary,
            cancelButtonColor: this.tenant.colors.secondary,
            confirmButtonText: "Download",
            cancelButtonText: "Cancel",
            focusConfirm: false,
            html: `
            <div class="d-flex flex-column align-items-center">
              
              <strong class="mt-3">Index Attribute #1 <span class="text-primary">*</span></strong>
              <select class="mt-1" id="pivot-index">
                <option value=""></option>
                ${headers.map((h) => `<option value="${h}">${h}</option>`)}
              </select>
              
              <strong class="mt-3">Index Attribute #2 (optional)</strong>
              <select class="mt-1" id="pivot-index-2">
                <option value=""></option>
                ${headers.map((h) => `<option value="${h}">${h}</option>`)}
              </select>
              
              <strong class="mt-3">Column Attribute <span class="text-primary">*</span></strong>
              <select class="mt-1" id="pivot-column">
                <option value=""></option>
                ${headers.map((h) => `<option value="${h}">${h}</option>`)}
              </select>
              
              <strong class="mt-3">Value Attribute <span class="text-primary">*</span></strong>
              <select class="mt-1" id="pivot-value">
                <option value=""></option>
                ${headers.map((h) => `<option value="${h}">${h}</option>`)}
              </select>
              
              <strong class="mt-3">Aggregation Operation <span class="text-primary">*</span></strong>
              <select class="mt-1" id="pivot-agg">
                ${["SUM", "COUNT", "MAX", "MIN", "AVG"].map((h) => `<option value="${h}">${h}</option>`)}
              </select>
              
            </div>
          `,
            preConfirm: () => {
              return [
                (document.getElementById("pivot-index") as HTMLSelectElement)?.value,
                (document.getElementById("pivot-index-2") as HTMLSelectElement)?.value,
                (document.getElementById("pivot-column") as HTMLSelectElement)?.value,
                (document.getElementById("pivot-value") as HTMLSelectElement)?.value,
                (document.getElementById("pivot-agg") as HTMLSelectElement)?.value,
              ];
            },
          });

          if (!responsePivot.isConfirmed) {
            return;
          }

          const [pivotIndex, pivotIndex2, pivotColumn, pivotValue, pivotAggFn] = (responsePivot?.value || []).map(
            (s: string | undefined) => {
              return s?.trim();
            }
          );
          if (!pivotIndex || !pivotColumn || !pivotValue || !pivotAggFn) {
            await Swal.fire({
              title: "Please select columns",
              cancelButtonColor: this.tenant.colors.secondary,
              showConfirmButton: false,
              showCancelButton: true,
              allowOutsideClick: false,
              allowEscapeKey: false,
              backdrop: true,
              confirmButtonText: "Return",
            });
            return;
          }
          return await this.download(
            this.convertCsvTableToPivotTable(data, pivotIndex, pivotIndex2, pivotColumn, pivotValue, pivotAggFn),
            fileName,
            mime,
            true
          );
        }
      }
    }

    const pom = document.createElement("a");
    const blob = new Blob(["\ufeff", parseCSV], { type: `${mime};charset=utf-8;` });
    pom.href = URL.createObjectURL(blob);
    pom.setAttribute(
      "download",
      fileName
        .split(" ")
        .filter(Boolean)
        .map((w) => w.split("-"))
        .flat()
        .filter(Boolean)
        .map((w, idx) => (w === csp || (idx === 0 && w.length === 3) ? w.toUpperCase() : w))
        .join("-")
        .replace(/\.\d+Z/i, "")
    );
    pom.click();
  }

  private isEligibleForPivotHeaders(headers: string[]): boolean {
    return (headers.filter((h) => !(h || "").toLowerCase().includes("formatted"))?.length || 0) >= 3;
  }

  private isEligibleForPivotRows(csvString: string): boolean {
    let n = 0;
    for (let i = 0; i < csvString.length; i++) {
      if (n >= 5) {
        break;
      }
      if (csvString[i] === "\n") {
        n++;
      }
    }
    return n >= 5;
  }
}
